import { useCallback, useContext } from 'react';
import type { UseMutationConfig } from 'react-relay';
import type { MutationParameters } from 'relay-runtime';

import InteractionContext, { type InteractionContextType } from '@atlaskit/interaction-context';
import {
	getActiveInteraction,
	type InteractionMetrics,
} from '@atlaskit/react-ufo/interaction-metrics';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { setInteractionError } from '@atlassian/ufo-set-interaction-error';
import type { FetchError } from '@atlassian/jira-fetch/src/index.tsx';
import { isFetchError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import {
	startCapturingExtensions,
	stopCapturingExtensions,
	getExtensions,
} from '@atlassian/relay-capture-extensions';
import {
	useApplication,
	useEdition,
	useProjectKey,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import {
	useAnalyticsSource,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { isAbortableError } from '@atlassian/jira-fetch/src/utils/is-abortable-error.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics/src/index.tsx';
import { extractFirstErrorFromRelayError } from '@atlassian/jira-issue-view-layout-experience-tracking/src/field-experience-tracking/index.tsx';
import type {
	AdditionalEventAttributes,
	ExperienceDescription,
} from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';

type AGGError = {
	message: string;
	extensions?: {
		statusCode?: number;
	};
};

type AGGResponse = {
	success: boolean;
	errors?: AGGError[];
	jira: {
		[key: string]: {
			errors?: AGGError[];
		};
	};
};

type Extensions = {
	gateway: {
		trace_id: string;
	};
};

const isExtensions = (value: unknown): value is Extensions => {
	return (
		typeof value === 'object' &&
		value !== null &&
		'gateway' in value &&
		typeof value.gateway === 'object' &&
		value.gateway !== null &&
		'trace_id' in value.gateway &&
		typeof value.gateway.trace_id === 'string'
	);
};

const isAGGResponse = (value: unknown): value is AGGResponse => {
	return (
		typeof value === 'object' &&
		value !== null &&
		(('jira' in value && typeof value.jira === 'object' && value.jira !== null) ||
			('errors' in value && typeof value.errors === 'object'))
	);
};

const extractError = (response: AGGResponse) => {
	if (response.errors) {
		return response.errors;
	}

	for (const key in response.jira) {
		if (response.jira[key]) {
			const value = response.jira[key];
			if (value.errors) {
				return value.errors;
			}
		}
	}

	return [];
};

const stopCapturingAndGetTraceId = (mutationName: string) => {
	const [extensions] = getExtensions(mutationName);
	stopCapturingExtensions(mutationName);
	return isExtensions(extensions) ? extensions.gateway.trace_id : 'unknown';
};

const isAbortableStatusCode = (statusCode?: number) => {
	return statusCode && statusCode >= 400 && statusCode < 500;
};

const stopCapturingAndGetMetaData = (mutationName: string, response?: AGGResponse) => {
	const traceId = stopCapturingAndGetTraceId(mutationName);
	const aggError = response ? extractError(response)[0] : undefined;
	const statusCode = aggError?.extensions?.statusCode;
	const errorMessage = aggError?.message;

	return { traceId, errorMessage, statusCode, aggError };
};

const maybeSendUFOErrorAndReleaseHold = (
	activeInteraction: InteractionMetrics | undefined,
	ufoHoldCleanup: ReturnType<InteractionContextType['hold']>,
	traceId: string,
	sendUFOEvent?: boolean,
	hasError?: boolean,
	statusCode?: number,
	errorMessage?: string,
) => {
	if (sendUFOEvent) {
		if (hasError && activeInteraction?.ufoName) {
			addUFOCustomData({
				traceId,
				statusCode,
			});
			setInteractionError(activeInteraction?.ufoName, {
				errorMessage: errorMessage || 'unknown',
				name: errorMessage || 'unknown',
			});
		}
		ufoHoldCleanup?.();
	}
};

export const useIssueExperienceMonitoring = () => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);
	const analyticsSource = useAnalyticsSource();
	const context = useContext(InteractionContext);

	const enrichCommitWithIssueExperienceMonitoring = useCallback(
		<T extends MutationParameters>(
			commitArgs: UseMutationConfig<T>,
			mutationName: string,
			{
				sendUFOEvent,
				additionalAttributes,
				task,
			}: {
				sendUFOEvent?: boolean;
				additionalAttributes?: AdditionalEventAttributes;
				task?: string;
			} = {},
			shouldWrapWithAnalytics = () => true,
		): UseMutationConfig<T> => {
			if (!shouldWrapWithAnalytics()) {
				return commitArgs;
			}

			startCapturingExtensions(mutationName);
			let activeInteraction: InteractionMetrics | undefined;
			let ufoHoldCleanup: ReturnType<InteractionContextType['hold']>;
			if (sendUFOEvent) {
				activeInteraction = getActiveInteraction();
				ufoHoldCleanup = context?.hold(activeInteraction?.ufoName);
			}

			const defaultExperienceArgs = {
				analyticsSource: analyticsSource || 'unknown',
				application: application || null,
				edition: edition || null,
				additionalAttributes,
			};

			return {
				...commitArgs,
				onCompleted: (response, error) => {
					const aggResponse = isAGGResponse(response) ? response : undefined;
					const { traceId, aggError, errorMessage, statusCode } = stopCapturingAndGetMetaData(
						mutationName,
						aggResponse,
					);
					let action: ExperienceDescription['action'] = 'taskSuccess';
					if (aggError) {
						action = isAbortableStatusCode(statusCode) ? 'taskAbort' : 'taskFail';
					}

					commitArgs.onCompleted?.(response, error);
					maybeSendUFOErrorAndReleaseHold(
						activeInteraction,
						ufoHoldCleanup,
						traceId,
						sendUFOEvent,
						!!aggError,
						statusCode,
						errorMessage,
					);

					if (task) {
						sendExperienceAnalytics({
							...defaultExperienceArgs,
							experience: task,
							action,
							additionalAttributes: {
								...additionalAttributes,
								...(action !== 'taskSuccess' && {
									traceId,
									...(errorMessage !== undefined && { errorMessage }),
									...(statusCode !== undefined && { statusCode }),
								}),
							},
						});
					}
				},
				onError: (error: Error | FetchError) => {
					const traceId = stopCapturingAndGetTraceId(mutationName);
					const aggError = extractFirstErrorFromRelayError(error);
					const errorMessage = aggError?.message || error?.message;
					const statusCode = isFetchError(error) ? error?.statusCode : aggError?.statusCode;
					const hasError = !!aggError || !!isFetchError(error);
					const action =
						isAbortableError(error) || isAbortableStatusCode(statusCode) ? 'taskAbort' : 'taskFail';

					commitArgs.onError?.(error);
					maybeSendUFOErrorAndReleaseHold(
						activeInteraction,
						ufoHoldCleanup,
						traceId,
						sendUFOEvent,
						hasError,
						statusCode,
						errorMessage,
					);

					if (task) {
						sendExperienceAnalytics({
							...defaultExperienceArgs,
							experience: task,
							action,
							additionalAttributes: {
								...additionalAttributes,
								traceId,
								...(errorMessage !== undefined && { errorMessage }),
								...(statusCode !== undefined && { statusCode }),
							},
						});
					}
				},
			};
		},
		[analyticsSource, application, edition, context],
	);

	return {
		enrichCommitWithIssueExperienceMonitoring,
	};
};
